import React, { useEffect, useState } from "react";
import { AlertCircle, ChevronLeft, ChevronRight, Loader2, Play, Trash2 } from "lucide-react";
import { assetUrl } from "../../lib/api";
import { secs } from "../../hooks/useVideoStudio";

const TrimInput = ({ label, value, onChange, onCommit, max, testid }) => (
  <label className="flex-1 text-[10px] text-slate-500">
    {label}
    <input type="number" min={0} max={max} step={0.1} value={value} onChange={(e) => onChange(e.target.value)} onBlur={onCommit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} data-testid={testid}
      className="mt-0.5 w-full h-7 rounded-md border border-slate-200 px-1.5 text-xs text-slate-800 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
  </label>
);

const Status = ({ clip }) => clip.status === "generating" ? (
  <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-[11px] text-slate-500"><Loader2 size={16} className="animate-spin text-emerald-600" />Making… 1–3 min</div>
) : (
  <div className="w-full h-full flex flex-col items-center justify-center gap-1 px-2 text-center text-[10px] text-rose-600" data-testid="clip-error"><AlertCircle size={15} />{clip.error || "Didn't finish"}</div>
);

export const ClipCard = ({ clip, index, count, active, onPlay, onMove, onRemove, onTrim }) => {
  const dur = Number(clip.duration || 0);
  const [s, setS] = useState(clip.trim_start ?? 0);
  const [e, setE] = useState(clip.trim_end ?? dur);
  useEffect(() => { setS(clip.trim_start ?? 0); setE(clip.trim_end ?? dur); }, [clip.trim_start, clip.trim_end, dur]);
  const commit = () => {
    const a = Math.max(0, Math.min(Number(s) || 0, dur)), b = Math.max(0, Math.min(Number(e) || dur, dur));
    if (a !== clip.trim_start || b !== clip.trim_end) onTrim(a, b);
  };
  const ready = clip.status === "ready";

  return (
    <div className={`w-48 shrink-0 rounded-xl border bg-white overflow-hidden transition-shadow ${active ? "border-emerald-500 ring-1 ring-emerald-500/30" : "border-slate-200"}`} data-testid={`clip-card-${index}`}>
      <div className="relative h-24 bg-slate-100">
        {ready ? (
          <button onClick={onPlay} data-testid={`clip-play-${index}`} className="group w-full h-full">
            {clip.thumb_url && <img src={assetUrl(clip.thumb_url)} alt={`Clip ${index + 1}`} className="w-full h-full object-cover" />}
            <span className="absolute inset-0 flex items-center justify-center bg-slate-900/0 group-hover:bg-slate-900/30 transition-colors"><Play size={20} className="text-white opacity-80 group-hover:opacity-100" /></span>
          </button>
        ) : <Status clip={clip} />}
        <span className="absolute top-1.5 left-1.5 text-[10px] font-mono bg-slate-900/70 text-white rounded px-1.5">{index + 1}</span>
        {ready && <span className="absolute bottom-1.5 right-1.5 text-[10px] font-mono bg-slate-900/70 text-white rounded px-1.5" data-testid={`clip-length-${index}`}>{secs((clip.trim_end || dur) - (clip.trim_start || 0))}</span>}
      </div>
      <div className="p-2 space-y-2">
        <p className="text-[11px] text-slate-600 line-clamp-2 min-h-[2.2em]" title={clip.prompt}>{clip.prompt}</p>
        {ready && <div className="flex gap-1.5">
          <TrimInput label="From (s)" value={s} onChange={setS} onCommit={commit} max={dur} testid={`clip-trim-start-${index}`} />
          <TrimInput label="To (s)" value={e} onChange={setE} onCommit={commit} max={dur} testid={`clip-trim-end-${index}`} />
        </div>}
        <div className="flex items-center gap-1 text-slate-400">
          <button onClick={() => onMove(-1)} disabled={index === 0} data-testid={`clip-left-${index}`} aria-label="Move earlier" className="p-1 rounded hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"><ChevronLeft size={14} /></button>
          <button onClick={() => onMove(1)} disabled={index === count - 1} data-testid={`clip-right-${index}`} aria-label="Move later" className="p-1 rounded hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"><ChevronRight size={14} /></button>
          <span className="ml-auto text-[10px] capitalize">{clip.quality}</span>
          {clip.status !== "generating" && <button onClick={onRemove} data-testid={`clip-remove-${index}`} aria-label="Remove clip" className="p-1 rounded hover:bg-rose-50 hover:text-rose-600"><Trash2 size={13} /></button>}
        </div>
      </div>
    </div>
  );
};

export default ClipCard;
